import {
  collection,
  getDocs,
  query,
  where,
  writeBatch,
  doc,
} from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { Match, NewMatch } from '../../types';
import { MundialMatchType, MundialTeam, MUNDIAL_LEAGUE } from '../../types/mundial';
import { PENDING_TEAM_LABEL } from '../../constants/mundialBracket';
import { hydrateMundialKnockoutTeams } from './mundialKnockoutHydrationService';

export const mundialMatchService = {
  async getMundialMatches(): Promise<Match[]> {
    const ref = collection(db, 'matches');
    const q = query(ref, where('league', '==', MUNDIAL_LEAGUE));
    const snap = await getDocs(q);
    return snap.docs.map((d) => {
      const data = d.data();
      return {
        id: d.id,
        ...data,
        date: data.date?.toDate?.() ?? data.date,
        created_at: data.created_at?.toDate?.() ?? data.created_at,
        updated_at: data.updated_at?.toDate?.() ?? data.updated_at,
      } as Match;
    });
  },

  /** Partidos con equipos de eliminatoria resueltos desde source_slot (Excel), sin escribir en BD */
  async getMundialMatchesHydrated(): Promise<Match[]> {
    const matches = await this.getMundialMatches();
    return hydrateMundialKnockoutTeams(matches);
  },

  async getByType(matchType: MundialMatchType): Promise<Match[]> {
    const all = await this.getMundialMatches();
    return all
      .filter((m) => m.match_type === matchType)
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  },

  async getGroupMatches(group?: string): Promise<Match[]> {
    const all = await this.getMundialMatches();
    return all
      .filter(
        (m) =>
          m.match_type === 'group' &&
          (!group || m.group?.toUpperCase() === group.toUpperCase())
      )
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  },

  enrichMatchLogos(match: Match, teamsByCode: Map<string, MundialTeam>): Match {
    const codeA = match.team_a_code?.toUpperCase();
    const codeB = match.team_b_code?.toUpperCase();
    const teamA = codeA ? teamsByCode.get(codeA) : undefined;
    const teamB = codeB ? teamsByCode.get(codeB) : undefined;
    return {
      ...match,
      team_a_logo: teamA?.flag_url ?? match.team_a_logo,
      team_b_logo: teamB?.flag_url ?? match.team_b_logo,
    };
  },
};

/** Genera las 6 fechas de un grupo de 4 equipos (todos contra todos) */
export function generateGroupFixtures(
  group: string,
  teams: MundialTeam[],
  baseDate: Date
): Omit<NewMatch, 'id'>[] {
  const codes = teams.map((t) => t.code);
  const pairs: [number, number][] = [];
  for (let i = 0; i < codes.length; i++) {
    for (let j = i + 1; j < codes.length; j++) {
      pairs.push([i, j]);
    }
  }

  return pairs.map(([homeIdx, awayIdx], matchIndex) => {
    const home = teams[homeIdx];
    const away = teams[awayIdx];
    const jornada = Math.floor(matchIndex / 2) + 1;
    const date = new Date(baseDate);
    date.setDate(date.getDate() + jornada - 1);

    return {
      league: MUNDIAL_LEAGUE,
      phase: `Grupo ${group}`,
      group: group.toUpperCase(),
      match_type: 'group' as MundialMatchType,
      jornada,
      team_a: home.name,
      team_b: away.name,
      team_a_code: home.code,
      team_b_code: away.code,
      team_a_logo: home.flag_url,
      team_b_logo: away.flag_url,
      score_a: null,
      score_b: null,
      activo: jornada === 1,
      date,
      is_first_leg: true,
      stadium: null,
      bracket_slot: null,
      created_at: new Date(),
      updated_at: new Date(),
    };
  });
}

export async function seedGroupMatchesForTeams(
  teams: MundialTeam[],
  startDate: Date
): Promise<number> {
  const byGroup = new Map<string, MundialTeam[]>();
  teams.forEach((t) => {
    const g = t.group.toUpperCase();
    if (!byGroup.has(g)) byGroup.set(g, []);
    byGroup.get(g)!.push(t);
  });

  const batch = writeBatch(db);
  let count = 0;
  const groupOffset: Record<string, number> = {};

  byGroup.forEach((groupTeams, group) => {
    if (groupTeams.length < 4) return;
    const offset = groupOffset[group] ?? 0;
    const base = new Date(startDate);
    base.setDate(base.getDate() + offset * 7);
    const fixtures = generateGroupFixtures(group, groupTeams.slice(0, 4), base);
    fixtures.forEach((fixture) => {
      const ref = doc(collection(db, 'matches'));
      batch.set(ref, fixture);
      count += 1;
    });
    groupOffset[group] = (groupOffset[group] ?? 0) + 1;
  });

  if (count > 0) await batch.commit();
  return count;
}

export function buildKnockoutPlaceholder(
  bracketSlot: string,
  matchType: MundialMatchType,
  phaseLabel: string,
  date: Date,
  stadium?: string
): Omit<NewMatch, 'id'> {
  return {
    league: MUNDIAL_LEAGUE,
    phase: phaseLabel,
    match_type: matchType,
    bracket_slot: bracketSlot,
    team_a: PENDING_TEAM_LABEL,
    team_b: PENDING_TEAM_LABEL,
    team_a_code: null,
    team_b_code: null,
    score_a: null,
    score_b: null,
    activo: false,
    date,
    is_first_leg: true,
    stadium: stadium ?? null,
    group: null,
    created_at: new Date(),
    updated_at: new Date(),
  };
}
